/**
 * Admin Player Pool Panel
 * Manage player pool for auction
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import { formatBYC, PLAYER_STATUS_COLORS, getErrorMessage } from './useAuction';

export default function PlayerPoolPanel({ eventId, config, teams, onPoolChange }) {
  const [players, setPlayers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [generating, setGenerating] = useState(false);
  const [randomizing, setRandomizing] = useState(false);
  const [showIconModal, setShowIconModal] = useState(null);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showDirectAssignModal, setShowDirectAssignModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(null);
  const [photoOverlay, setPhotoOverlay] = useState(null);
  
  const canEdit = config?.auction_status === 'DRAFT' || config?.auction_status === 'CONFIGURED';
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin' || (user.roles || []).includes('super_admin');
  const showActions = canEdit || isSuperAdmin;
  const canGenerate = config?.auction_status === 'DRAFT'; // Pool can only be generated in DRAFT status

  const fetchPool = useCallback(async () => {
    try {
      setLoading(true);
      const [poolRes, statsRes] = await Promise.all([
        axiosInstance.get(`/v1/events/${eventId}/auction/pool`),
        axiosInstance.get(`/v1/events/${eventId}/auction/pool/stats`).catch(() => ({ data: null }))
      ]);
      setPlayers(poolRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        toast.error('Failed to load player pool');
      }
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchPool();
  }, [fetchPool]);

  const handleGeneratePool = async () => {
    if (!window.confirm('Generate player pool from approved registrations? This will create pool entries for all eligible players.')) {
      return;
    }
    try {
      setGenerating(true);
      const res = await axiosInstance.post(`/v1/events/${eventId}/auction/pool/generate`, { confirm: true });
      toast.success(`Pool generated: ${res.data.players_added} players added`);
      fetchPool();
      onPoolChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to generate pool'));
    } finally {
      setGenerating(false);
    }
  };

  const handleRandomize = async () => {
    try {
      setRandomizing(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/pool/randomize`, { confirm: true });
      toast.success('Pool order randomized');
      fetchPool();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to randomize'));
    } finally {
      setRandomizing(false);
    }
  };

  const handleMarkIcon = async (playerId, isIcon) => {
    try {
      if (isIcon) {
        await axiosInstance.delete(`/v1/events/${eventId}/auction/pool/${playerId}/mark-icon`);
        toast.success('Icon status removed');
      } else {
        await axiosInstance.post(`/v1/events/${eventId}/auction/pool/${playerId}/mark-icon`);
        toast.success('Marked as Icon player');
      }
      fetchPool();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update icon status'));
    }
  };

  const handleRemoveIconAssignment = async (playerId) => {
    try {
      await axiosInstance.post(`/v1/events/${eventId}/auction/pool/${playerId}/remove-icon-assignment`);
      toast.success('Icon assignment removed and fee refunded');
      fetchPool();
      onPoolChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove icon assignment');
    }
  };

  const handleRemoveFromPool = async (playerId, playerName) => {
    try {
      await axiosInstance.delete(`/v1/events/${eventId}/auction/pool/${playerId}`);
      toast.success(`${playerName} removed from pool`);
      fetchPool();
      onPoolChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to remove player');
    }
  };

  const filteredPlayers = players.filter(p => {
    if (filter === 'ALL') return true;
    return p.status === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3AAFA9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="player-pool-panel">
      {/* Header with Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="font-bold text-lg">Player Pool ({players.length})</h3>
          <p className="text-sm text-gray-500">Manage players available for auction</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isSuperAdmin && (
            <>
              <button
                onClick={() => setShowAddPlayerModal(true)}
                className="px-4 py-2 bg-[#2B3A67] text-white rounded-lg font-bold text-sm hover:bg-[#1e2a4a]"
                data-testid="add-player-btn"
              >
                <i className="ph-bold ph-user-plus mr-2"></i>Add Player
              </button>
              <button
                onClick={() => setShowDirectAssignModal(true)}
                className="px-4 py-2 bg-[#E54562] text-white rounded-lg font-bold text-sm hover:bg-[#d43a55]"
                data-testid="direct-assign-btn"
              >
                <i className="ph-bold ph-arrow-right mr-2"></i>Direct Assign
              </button>
            </>
          )}
          {players.length > 0 && canEdit && (
            <button
              onClick={handleRandomize}
              disabled={randomizing}
              className="px-4 py-2 border border-gray-200 text-gray-700 rounded-lg font-bold text-sm hover:bg-gray-50 disabled:opacity-50"
              data-testid="randomize-pool-btn"
            >
              <i className="ph-bold ph-shuffle mr-2"></i>
              {randomizing ? 'Randomizing...' : 'Randomize'}
            </button>
          )}
          {players.length === 0 && canGenerate && (
            <button
              onClick={handleGeneratePool}
              disabled={generating}
              className="px-4 py-2 bg-[#3AAFA9] text-white rounded-lg font-bold text-sm hover:bg-[#2d8a85] disabled:opacity-50"
              data-testid="generate-pool-btn"
            >
              <i className="ph-bold ph-users-three mr-2"></i>
              {generating ? 'Generating...' : 'Generate Pool'}
            </button>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total" value={stats.total_players || stats.total || 0} color="bg-gray-100 text-gray-700" />
          <StatCard label="Available" value={stats.available || 0} color="bg-green-100 text-green-700" />
          <StatCard label="Sold" value={stats.sold || 0} color="bg-blue-100 text-blue-700" />
          <StatCard label="Unsold" value={stats.unsold || 0} color="bg-red-100 text-red-700" />
          <StatCard label="Icons" value={stats.icon_players || stats.icons || 0} color="bg-purple-100 text-purple-700" />
        </div>
      )}

      {/* Filter Tabs */}
      {players.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {['ALL', 'AVAILABLE', 'SOLD', 'UNSOLD', 'EXCLUDED'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-colors ${
                filter === f
                  ? 'bg-[#2B3A67] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      {/* Player List */}
      {players.length === 0 ? (
        <div className="bg-white rounded-xl p-8 border border-gray-100 text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <i className="ph-bold ph-users text-2xl text-gray-400"></i>
          </div>
          <p className="text-gray-500 mb-2">No players in pool</p>
          <p className="text-sm text-gray-400">Generate pool from approved player registrations</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Player</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Base Price</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase">Team</th>
                  {showActions && <th className="px-4 py-3 text-right text-xs font-bold text-gray-500 uppercase">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredPlayers.map((player, idx) => (
                  <tr key={player.id} className="hover:bg-gray-50" data-testid={`player-row-${player.id}`}>
                    <td className="px-4 py-3 text-sm text-gray-500">{player.pool_order || idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {player.player_photo_url ? (
                          <button
                            onClick={() => setPhotoOverlay({ url: player.player_photo_url, name: player.player_name })}
                            className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-transparent hover:ring-[#3AAFA9] transition-all cursor-pointer"
                            title="Click to view photo"
                          >
                            <img src={player.player_photo_url} alt={player.player_name} className="w-full h-full object-cover" />
                          </button>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#2B3A67] flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-xs font-bold">
                              {player.player_name?.[0] || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm">{player.player_name}</p>
                          <div className="flex items-center gap-1">
                            {player.is_icon && (
                              <span className="text-[10px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded font-bold">
                                ICON
                              </span>
                            )}
                            {player.is_owner_player && (
                              <span className="text-[10px] bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded font-bold">
                                OWNER
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">₿ {formatBYC(player.base_price_byc)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${PLAYER_STATUS_COLORS[player.status] || 'bg-gray-100'}`}>
                        {player.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {player.sold_to_team_name || player.icon_assigned_team_name || '-'}
                    </td>
                    {showActions && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canEdit && config?.enable_icon_players && player.status === 'AVAILABLE' && !player.is_owner_player && (
                            <>
                              <button
                                onClick={() => handleMarkIcon(player.id, player.is_icon)}
                                className={`px-2 py-1 rounded text-xs font-bold ${
                                  player.is_icon
                                    ? 'bg-purple-100 text-purple-600'
                                    : 'border border-purple-200 text-purple-600 hover:bg-purple-50'
                                }`}
                                title={player.is_icon ? 'Remove Icon' : 'Mark as Icon'}
                              >
                                <i className={`ph-bold ph-star${player.is_icon ? '-fill' : ''}`}></i>
                              </button>
                              {player.is_icon && !player.icon_assigned_team_id && (
                                <button
                                  onClick={() => setShowIconModal(player)}
                                  className="px-2 py-1 rounded text-xs font-bold border border-[#3AAFA9] text-[#3AAFA9] hover:bg-[#3AAFA9]/10"
                                  title="Assign to Team"
                                >
                                  Assign
                                </button>
                              )}
                            </>
                          )}
                          {player.is_icon && player.icon_assigned_team_id && (
                            <button
                              onClick={() => handleRemoveIconAssignment(player.id)}
                              className="px-2 py-1 rounded text-xs font-bold border border-[#E54562] text-[#E54562] hover:bg-[#E54562]/10"
                              title="Remove icon assignment and refund"
                              data-testid={`remove-icon-${player.id}`}
                            >
                              <i className="ph-bold ph-x-circle mr-1"></i>Remove Icon
                            </button>
                          )}
                          {isSuperAdmin && player.sold_to_team_id && (
                            <button
                              onClick={() => setShowReassignModal(player)}
                              className="px-2 py-1 rounded text-xs font-bold border border-amber-400 text-amber-600 hover:bg-amber-50"
                              title="Reassign to different team"
                              data-testid={`reassign-${player.id}`}
                            >
                              <i className="ph-bold ph-arrows-left-right mr-1"></i>Reassign
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button
                              onClick={() => handleRemoveFromPool(player.id, player.player_name)}
                              className="px-2 py-1 rounded text-xs font-bold border border-gray-300 text-gray-500 hover:bg-red-50 hover:border-red-300 hover:text-red-500"
                              title="Remove player from pool"
                              data-testid={`remove-pool-${player.id}`}
                            >
                              <i className="ph-bold ph-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Icon Assignment Modal */}
      {showIconModal && (
        <IconAssignModal
          eventId={eventId}
          player={showIconModal}
          teams={teams}
          players={players}
          iconPrice={config?.icon_player_fixed_price_byc || 0}
          onClose={() => setShowIconModal(null)}
          onAssigned={() => { fetchPool(); onPoolChange?.(); }}
        />
      )}

      {/* Add Player Modal */}
      {showAddPlayerModal && (
        <AddPlayerModal
          eventId={eventId}
          config={config}
          pool={players}
          onClose={() => setShowAddPlayerModal(false)}
          onAdded={() => { fetchPool(); onPoolChange?.(); setShowAddPlayerModal(false); }}
        />
      )}

      {/* Direct Assign Modal */}
      {showDirectAssignModal && (
        <DirectAssignModal
          eventId={eventId}
          teams={teams}
          players={players}
          onClose={() => setShowDirectAssignModal(false)}
          onAssigned={() => { fetchPool(); onPoolChange?.(); setShowDirectAssignModal(false); }}
        />
      )}

      {/* Reassign Modal */}
      {showReassignModal && (
        <ReassignPlayerModal
          eventId={eventId}
          player={showReassignModal}
          teams={teams}
          onClose={() => setShowReassignModal(null)}
          onReassigned={() => { fetchPool(); onPoolChange?.(); setShowReassignModal(null); }}
        />
      )}

      {/* Photo Overlay */}
      {photoOverlay && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setPhotoOverlay(null)}
          data-testid="photo-overlay"
        >
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPhotoOverlay(null)}
              className="absolute -top-10 right-0 text-white/70 hover:text-white text-sm font-bold"
            >
              <i className="ph-bold ph-x text-xl"></i>
            </button>
            <img
              src={photoOverlay.url}
              alt={photoOverlay.name}
              className="w-full rounded-xl shadow-2xl"
            />
            <p className="text-white text-center mt-3 font-bold">{photoOverlay.name}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({ label, value, color }) {
  return (
    <div className={`rounded-xl p-3 ${color}`}>
      <p className="text-2xl font-extrabold">{value}</p>
      <p className="text-xs font-medium opacity-80">{label}</p>
    </div>
  );
}

// Icon Assignment Modal
function IconAssignModal({ eventId, player, teams, players, iconPrice, onClose, onAssigned }) {
  const [selectedTeam, setSelectedTeam] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAssign = async () => {
    if (!selectedTeam) return;
    try {
      setSaving(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/pool/${player.id}/assign-icon-team`, {
        team_id: selectedTeam
      });
      toast.success(`Icon player assigned to team`);
      onAssigned();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to assign icon'));
    } finally {
      setSaving(false);
    }
  };

  // Filter teams: sufficient balance AND not already having an icon player assigned
  const teamsWithIconAlready = new Set(
    (players || []).filter(p => p.is_icon && p.icon_assigned_team_id).map(p => p.icon_assigned_team_id)
  );
  const eligibleTeams = teams.filter(t => 
    (t.wallet_balance_byc || t.wallet?.balance_byc || 0) >= iconPrice && !teamsWithIconAlready.has(t.id)
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="icon-assign-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-lg">Assign Icon Player</h3>
            <p className="text-sm text-gray-500">Assign {player.player_name} to a team</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1 flex-shrink-0" aria-label="Close" data-testid="icon-assign-close">×</button>
        </div>
        <div className="p-5">
          <div className="mb-4 p-3 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-700">
              <strong>Icon Price:</strong> ₿ {formatBYC(iconPrice)}
            </p>
            <p className="text-xs text-purple-600 mt-1">
              This amount will be deducted from the team's wallet
            </p>
          </div>

          {eligibleTeams.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-gray-500">No teams have sufficient balance</p>
            </div>
          ) : (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Team</label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none"
                data-testid="icon-team-select"
              >
                <option value="">Select a team...</option>
                {eligibleTeams.map(team => (
                  <option key={team.id} value={team.id}>
                    {team.team_name} (₿ {formatBYC(team.wallet_balance_byc || team.wallet?.balance_byc || 0)})
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          {eligibleTeams.length > 0 && (
            <button
              onClick={handleAssign}
              disabled={saving || !selectedTeam}
              className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50"
              data-testid="icon-assign-submit"
            >
              {saving ? 'Assigning...' : 'Assign Icon'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// Add Player to Pool Modal
function AddPlayerModal({ eventId, config, pool, onClose, onAdded }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReg, setSelectedReg] = useState('');
  const [basePrice, setBasePrice] = useState(config?.standard_base_price_byc || 0);
  const [saving, setSaving] = useState(false);

  // Get registration IDs already in the pool to exclude them
  const poolRegIds = new Set((pool || []).map(p => p.registration_id).filter(Boolean));

  useEffect(() => {
    const fetchRegs = async () => {
      try {
        const res = await axiosInstance.get(`/v1/admin/events/${eventId}/registrations`);
        const allRegs = res.data.registrations || res.data || [];
        // Show player registrations that are approved/pending
        const playerRegs = allRegs.filter(r => 
          r.role === 'player' && 
          ['approved', 'pending'].includes(r.status)
        );
        setRegistrations(playerRegs);
      } catch {
        toast.error('Failed to load registrations');
      } finally {
        setLoading(false);
      }
    };
    fetchRegs();
  }, [eventId]);

  const handleAdd = async () => {
    if (!selectedReg) return;
    try {
      setSaving(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/pool/add-player`, {
        registration_id: selectedReg,
        base_price_byc: parseFloat(basePrice) || 0,
      });
      toast.success('Player added to pool');
      onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add player');
    } finally {
      setSaving(false);
    }
  };

  const notInPoolCount = registrations.filter(r => !poolRegIds.has(r.id)).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-player-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-lg">Add Player to Pool</h3>
            <p className="text-sm text-gray-500">Select a registered player not yet in the pool</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1 flex-shrink-0" aria-label="Close" data-testid="add-player-close">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Player *</label>
            {loading ? (
              <p className="text-sm text-gray-400">Loading registrations...</p>
            ) : (
              <select
                value={selectedReg}
                onChange={(e) => setSelectedReg(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none text-sm"
                data-testid="add-player-select"
              >
                <option value="">Select player... ({notInPoolCount} available)</option>
                {registrations.filter(r => !poolRegIds.has(r.id)).length > 0 && (
                  <optgroup label="Not in Pool">
                    {registrations.filter(r => !poolRegIds.has(r.id)).map(r => (
                      <option key={r.id} value={r.id}>
                        {r.registration_data?.player_name || r.registration_data?.name || r.user_name || r.user_email}
                      </option>
                    ))}
                  </optgroup>
                )}
                {registrations.filter(r => poolRegIds.has(r.id)).length > 0 && (
                  <optgroup label="Already in Pool">
                    {registrations.filter(r => poolRegIds.has(r.id)).map(r => (
                      <option key={r.id} value={r.id} disabled>
                        {r.registration_data?.player_name || r.registration_data?.name || r.user_name || r.user_email} (in pool)
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Base Price (BYC)</label>
            <input
              type="number"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              min={0}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none"
              data-testid="add-player-price"
            />
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleAdd}
            disabled={saving || !selectedReg}
            className="flex-1 py-2.5 bg-[#2B3A67] text-white rounded-xl font-bold hover:bg-[#1e2a4a] disabled:opacity-50"
            data-testid="add-player-submit"
          >
            {saving ? 'Adding...' : 'Add to Pool'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Direct Assign Player to Team Modal
function DirectAssignModal({ eventId, teams, players, onClose, onAssigned }) {
  const [selectedPlayer, setSelectedPlayer] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [price, setPrice] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const availablePlayers = players.filter(p => p.status === 'AVAILABLE');

  const handleAssign = async () => {
    if (!selectedPlayer || !selectedTeam) return;
    try {
      setSaving(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/direct-assign`, {
        player_pool_id: selectedPlayer,
        team_id: selectedTeam,
        price_byc: parseFloat(price) || 0,
        notes: notes || undefined,
      });
      toast.success('Player assigned to team');
      onAssigned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to assign player');
    } finally {
      setSaving(false);
    }
  };

  const selectedTeamData = teams?.find(t => t.id === selectedTeam);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="direct-assign-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-lg">Direct Assign Player</h3>
            <p className="text-sm text-gray-500">Assign a player directly to a team with wallet deduction</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1 flex-shrink-0" aria-label="Close" data-testid="direct-assign-close">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Player *</label>
            <select
              value={selectedPlayer}
              onChange={(e) => setSelectedPlayer(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none text-sm"
              data-testid="direct-assign-player-select"
            >
              <option value="">Select player...</option>
              {availablePlayers.map(p => (
                <option key={p.id} value={p.id}>{p.player_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Team *</label>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#3AAFA9] focus:ring-1 focus:ring-[#3AAFA9] outline-none text-sm"
              data-testid="direct-assign-team-select"
            >
              <option value="">Select team...</option>
              {(teams || []).map(t => (
                <option key={t.id} value={t.id}>{t.team_name} (₿ {formatBYC(t.wallet_balance_byc || 0)})</option>
              ))}
            </select>
            {selectedTeamData && (
              <p className="text-xs text-gray-400 mt-1">Balance: ₿ {formatBYC(selectedTeamData.wallet_balance_byc || 0)} · {selectedTeamData.remaining_slots ?? '?'} slots left</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Price (BYC) *</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Amount to deduct"
              min={0}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-[#E54562] focus:ring-1 focus:ring-[#E54562] outline-none"
              data-testid="direct-assign-price"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for direct assignment"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none"
            />
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleAssign}
            disabled={saving || !selectedPlayer || !selectedTeam || !price}
            className="flex-1 py-2.5 bg-[#E54562] text-white rounded-xl font-bold hover:bg-[#d43a55] disabled:opacity-50"
            data-testid="direct-assign-submit"
          >
            {saving ? 'Assigning...' : 'Assign Player'}
          </button>
        </div>
      </div>
    </div>
  );
}


// Reassign Player Modal
function ReassignPlayerModal({ eventId, player, teams, onClose, onReassigned }) {
  const [selectedTeam, setSelectedTeam] = useState('');
  const [price, setPrice] = useState(player.sold_price_byc || '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const currentTeam = teams?.find(t => t.id === player.sold_to_team_id);
  const availableTeams = (teams || []).filter(t => t.id !== player.sold_to_team_id);
  const selectedTeamData = teams?.find(t => t.id === selectedTeam);

  const handleReassign = async () => {
    if (!selectedTeam) return;
    try {
      setSaving(true);
      await axiosInstance.post(`/v1/events/${eventId}/auction/reassign-player`, {
        player_pool_id: player.id,
        new_team_id: selectedTeam,
        new_price_byc: parseFloat(price) || 0,
        notes: notes || undefined,
      });
      toast.success(`${player.player_name} reassigned`);
      onReassigned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reassign player');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="reassign-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-bold text-lg">Reassign Player</h3>
            <p className="text-sm text-gray-500">
              <span className="font-bold">{player.player_name}</span>
              {currentTeam && <> — currently on <span className="font-bold text-[#2B3A67]">{currentTeam.team_name}</span> for ₿ {formatBYC(player.sold_price_byc || 0)}</>}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none -mt-1 -mr-1 flex-shrink-0" aria-label="Close" data-testid="reassign-close">×</button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Team *</label>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none text-sm"
              data-testid="reassign-team-select"
            >
              <option value="">Select new team...</option>
              {availableTeams.map(t => (
                <option key={t.id} value={t.id}>{t.team_name} (₿ {formatBYC(t.wallet_balance_byc || 0)})</option>
              ))}
            </select>
            {selectedTeamData && (
              <p className="text-xs text-gray-400 mt-1">Balance: ₿ {formatBYC(selectedTeamData.wallet_balance_byc || 0)} · {selectedTeamData.remaining_slots ?? '?'} slots left</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Price (BYC)</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder={String(player.sold_price_byc || 0)}
              min={0}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none"
              data-testid="reassign-price"
            />
            <p className="text-xs text-gray-400 mt-1">Original price: ₿ {formatBYC(player.sold_price_byc || 0)}. Old team gets full refund.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reason for reassignment"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none"
            />
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex gap-3">
          <button onClick={onClose} className="flex-1 py-2.5 border border-gray-200 rounded-xl font-bold text-gray-600 hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleReassign}
            disabled={saving || !selectedTeam}
            className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 disabled:opacity-50"
            data-testid="reassign-submit"
          >
            {saving ? 'Reassigning...' : 'Reassign Player'}
          </button>
        </div>
      </div>
    </div>
  );
}
